import { useState, useRef, useEffect } from "react";
import {
  CommandPrimitive,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandEmpty,
  ScrollArea,
  cn,
} from "web-design-system";
import { useDebounceValue } from "usehooks-ts";
import { useClients } from "creso-core/hooks";
import { Client, ClientsFilter } from "creso-core/types";
import { getInitials } from "creso-core/utils";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";
import { Avatar } from "./avatars";
import { HoldingPatternBadge } from "./badges";

export interface ClientSearchSelectProps {
  onSelect: (client: Client) => void;
  placeholder?: string;
  pageSize?: number;
  emptyMessage?: string;
  autoFocus?: boolean;
  className?: string;
  excludeIds?: number[];
  clientsFilter?: ClientsFilter;
}

export function ClientSearchSelect({
  onSelect,
  clientsFilter = {},
  placeholder = "Search for Clients",
  pageSize = 20,
  emptyMessage = "No clients found.",
  autoFocus = true,
  className,
  excludeIds = [],
}: ClientSearchSelectProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const [isFocused, setIsFocused] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { data } = useClients(
    {
      search: debouncedSearch,
      pageSize,
      filters: clientsFilter,
    },
    { enabled: isFocused }
  );

  const excludeSet = new Set(excludeIds);
  const clients = data?.results ?? [];
  const showDropdown = isFocused;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        !listRef.current?.contains(e.target as Node)
      ) {
        setIsFocused(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (client: Client) => {
    setSearch("");
    setIsFocused(false);
    onSelect(client);
  };

  return (
    <CommandPrimitive shouldFilter={false} className={className}>
      <div ref={containerRef} className={cn("border border-border rounded-lg")}>
        <CommandInput
          placeholder={placeholder}
          className="py-2 px-0 h-10"
          value={search}
          autoFocus={autoFocus}
          onValueChange={setSearch}
          onFocus={() => setIsFocused(true)}
        />
      </div>

      <div className="relative z-10">
        <div
          className={cn(
            "absolute top-3 w-full rounded-lg bg-background border border-border",
            showDropdown ? "block" : "hidden"
          )}
        >
          <ScrollArea className="max-h-80">
            <CommandList ref={listRef} className="overflow-visible">
              <CommandGroup>
                {clients.map((client) => {
                  const isExcluded = excludeSet.has(client.id);
                  return (
                    <CommandItem
                      key={client.id}
                      value={String(client.id)}
                      disabled={isExcluded}
                      onSelect={() => {
                        if (isExcluded) return;
                        handleSelect(client);
                      }}
                      className="flex items-center gap-3 p-2 cursor-default"
                    >
                      <Avatar
                        url={""}
                        fallback={getInitials(client.name)}
                        size="sm"
                      />
                      <div className="flex flex-col min-w-0">
                        <div className="flex flex-row justify-start items-center gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {client.name}
                          </span>
                          {client.holding_pattern &&
                          JOINT_CLIENT_HOLDING_PATTERNS.includes(
                            client.holding_pattern
                          ) ? (
                            <HoldingPatternBadge
                              holdingPattern={client.holding_pattern}
                            />
                          ) : null}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {client.phone_number}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            </CommandList>
          </ScrollArea>
        </div>
      </div>
    </CommandPrimitive>
  );
}
